import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

/**
 * Page-cart flow follows successful native product-form additions. Inventory,
 * variant resolution and cart validation stay with the native product form.
 * Cart recommendations refresh after settled mutations without reloading the bag.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  if (!event.promise || !document.querySelector('cart-icon[data-cart-type="page"]')) return;

  try {
    const { detail } = await event.promise;
    if (detail?.didError !== false) return;

    const cartPage = document.querySelector('#MainContent[data-template="cart"]');
    if (!cartPage) {
      if (detail.source === 'product-form-component' && !window.Shopify?.designMode) {
        window.location.assign(Theme.routes.cart_url);
      }
      return;
    }

    const recommendationLists = cartPage.querySelectorAll('[data-oc-cart-recommendations]');
    const addedFromRecommendations =
      detail.source === 'product-form-component' &&
      event.target instanceof Element &&
      event.target.closest('[data-oc-cart-recommendations]') !== null;

    for (const list of recommendationLists) {
      const sectionId = list.getAttribute('data-oc-cart-recommendations');
      if (!sectionId) continue;

      // Quantities do not change which products are relevant. Avoid a full
      // recommendation fetch and DOM replacement for these frequent updates.
      if (detail.source === 'cart-items-component' && Array.isArray(detail.items) &&
          detail.items.every((item) => item.product_id != null) &&
          list.hasAttribute('data-oc-cart-product-ids')) {
        const currentIds = [...new Set(detail.items.map((item) => String(item.product_id)))].sort().join(',');
        const renderedIds = [...new Set(list.getAttribute('data-oc-cart-product-ids').split(',').filter(Boolean))].sort().join(',');
        if (currentIds === renderedIds) continue;
      }

      // Give cart totals and button feedback a chance to paint before the
      // non-critical recommendation section is rebuilt.
      await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
      await sectionRenderer.renderSection(sectionId, {
        cache: false,
        mode: 'full',
        injectStylesheet: true,
      });
    }

    // The added card is removed from suggestions; keep keyboard focus useful.
    if (addedFromRecommendations) {
      cartPage.querySelector('button[name="checkout"]')?.focus({ preventScroll: true });
    }
  } catch (error) {
    if (error?.name !== 'AbortError') console.warn('[cart-page] Cart experience update failed:', error);
  }
});
